// Types
import { EMRConnectionType } from "src/services/emr/types";

export type Translate = (
  key: string,
  options?: Record<string, string | number>
) => string;

export const DEFAULT_POLL_INTERVAL_MINUTES = 3;
export const POLL_INTERVAL_OPTIONS = [3, 5, 15, 30, 60, 120];
export const DEFAULT_IMPORT_TIMES = ["06:30", "12:00"];
export const WORKLIST_LOOKAHEAD_DAYS_MAX = 14;

const TIME_OF_DAY = /^([01]\d|2[0-3]):[0-5]\d$/;

export type WorklistTimingMode = "Interval" | "Times";

export type WorklistTimingValues = {
  mode: WorklistTimingMode;
  pollIntervalMinutes: number;
  importTimes: Array<string>;
  lookaheadDays: string;
};

export const DEFAULT_WORKLIST_TIMING: WorklistTimingValues = {
  mode: "Interval",
  pollIntervalMinutes: DEFAULT_POLL_INTERVAL_MINUTES,
  importTimes: DEFAULT_IMPORT_TIMES,
  lookaheadDays: "0",
};

export const isValidTimeOfDay = (time: string) => TIME_OF_DAY.test(time);

export const pollIntervalOptions = (currentMinutes: number) =>
  Array.from(new Set([...POLL_INTERVAL_OPTIONS, currentMinutes]))
    .sort((left, right) => left - right)
    .map((minutes) => ({
      displayName: String(minutes),
      value: String(minutes),
    }));

export const worklistTimingFromConfig = (
  config: EMRConnectionType["config"] | undefined
): WorklistTimingValues => {
  const importTimes = config?.importTimes ?? [];

  return {
    mode: importTimes.length > 0 ? "Times" : "Interval",
    pollIntervalMinutes:
      config?.pollIntervalMinutes ?? DEFAULT_POLL_INTERVAL_MINUTES,
    importTimes: importTimes.length > 0 ? importTimes : DEFAULT_IMPORT_TIMES,
    lookaheadDays: String(config?.lookaheadDays ?? 0),
  };
};

export const buildWorklistTiming = (
  timing: WorklistTimingValues
): {
  importTimes: Array<string>;
  pollIntervalMinutes: number;
  lookaheadDays: number;
} | null => {
  const lookaheadDays = Number(timing.lookaheadDays);
  const isLookaheadValid =
    timing.lookaheadDays.trim() !== "" &&
    Number.isInteger(lookaheadDays) &&
    lookaheadDays >= 0 &&
    lookaheadDays <= WORKLIST_LOOKAHEAD_DAYS_MAX;

  if (!isLookaheadValid) return null;

  if (timing.mode === "Interval") {
    return {
      importTimes: [],
      pollIntervalMinutes: timing.pollIntervalMinutes,
      lookaheadDays,
    };
  }

  const importTimes = Array.from(new Set(timing.importTimes)).sort();
  if (importTimes.length === 0 || !importTimes.every(isValidTimeOfDay)) {
    return null;
  }

  return {
    importTimes,
    pollIntervalMinutes: timing.pollIntervalMinutes,
    lookaheadDays,
  };
};

export const describeWorklistTiming = (
  config: EMRConnectionType["config"] | undefined,
  t: Translate
) => {
  const importTimes = config?.importTimes ?? [];

  if (importTimes.length > 0) {
    return t("Read at {{times}}", { times: importTimes.join(", ") });
  }

  return t("Read every {{minutes}} min", {
    minutes: config?.pollIntervalMinutes ?? DEFAULT_POLL_INTERVAL_MINUTES,
  });
};
